/*!
 * Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth, Tokens } from '@okta/okta-auth-js';
import OktaSignIn from '@okta/okta-signin-widget';
import { ApiService } from 'src/app/core/services/api.service';
import { OKTA_SIGNIN_WIDGET_I18N } from 'src/app/shared/constants/common.constants';
import { environment } from 'src/environments/environment';

const DEFAULT_ORIGINAL_URI = window.location.origin;

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: true,
})
export class LoginComponent implements OnInit, OnDestroy {

    private readonly signIn: OktaSignIn;


    constructor(@Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth, private readonly apiService: ApiService) {
        this.signIn = new OktaSignIn({

            baseUrl: environment.oktaIssuer,
            clientId: environment.oktaClientId,
            redirectUri: DEFAULT_ORIGINAL_URI + '/login/callback',
            useInteractionCodeFlow:false,
            //logo: 'assets/images/experian.png',
            i18n: {
                ...OKTA_SIGNIN_WIDGET_I18N
            },
            language: sessionStorage.getItem('currentLangCode') ?? 'en',
            authClient: oktaAuth,
            useClassicEngine: true,
        });
    //alert(DEFAULT_ORIGINAL_URI + '/login/callback');
    }

    async ngOnInit() {
        await this.loginOkta();
    }

    private async loginOkta() {
        this.oktaAuth.tokenManager.clear();

        const originalUri = this.oktaAuth.getOriginalUri();
        if (!originalUri || originalUri === DEFAULT_ORIGINAL_URI) {
            this.oktaAuth.setOriginalUri('/');
        }

        this.signIn.on('afterError', async (_context: any, error: any) => {
            if (error.xhr.responseJSON.errorCode === "E0000004") {
                const usernameInput = document.querySelector('#okta-signin-username') as HTMLInputElement;
                const userId = usernameInput.value;
                await this.apiService.post(`users/trace-incorrect-credentials?userId=${userId}`);
            }
        });

        await this.signIn.showSignInToGetTokens({ el: '#sign-in-widget' })
            .then( (result: Tokens) => {
                const originalUri = this.oktaAuth.getOriginalUri();
                if (originalUri === DEFAULT_ORIGINAL_URI) {
                    this.oktaAuth.setOriginalUri('/');
                }
                this.oktaAuth.setOriginalUri('/login-redirect');
                // Remove the widget
                this.signIn.remove();

                // In this flow the redirect to Okta occurs in a hidden iframe
                this.oktaAuth.handleLoginRedirect(result)
                    .then(async () => {
                        // const userClaims = await this.oktaAuth.getUser();
                        // sessionStorage.setItem('currentUser', "{ \"loginName\": \"" + Object.entries(userClaims)[1][1] + "\", \"appConfiguration\": {\"clientEnvironment\" : \"Sabadell\",\"documentationPacks\" : [ {\"packName\": \"AUTFIN01\", \"packDescription\":\"Pack Base\"}],\"availableQueries\": { \"requestors\": {\"physicalPerson\":true, \"legalEntity\": false},\"requests\": { \"clavePin\": false, \"clavePinWithSMS\": true, \"SMS\": false, \"videoId\": false, \"knownClient\": false} }}  }");
                    })
                    .catch(error =>
                        console.log(error)
                    );
            }).catch(err => {
            // Typically due to misconfiguration
                throw err;
            });
    }

    ngOnDestroy(): void {
        this.signIn.remove();
    }
}
